import { curry, orderBy } from 'lodash-es';
import dateFormat from 'dateformat';
import { MOVE_TYPES, ROUTES } from './constants';

const DAY_MS = 1000 * 60 * 60 * 24;

export const isOfferDate = date => ({ date: offerDate }) => date === offerDate;

const isAvailableOffer = offer => !!offer.id;

export const OFFER_SORTS = [
  {
    field: 'valueScore',
    order: 'desc',
  },
  {
    field: 'price',
    order: 'asc',
  },
];

function getSorts(sort) {
  // Secondary sort by `sort` and tertiary sort by the remaining sort type.
  return [sort].concat(
    // TODO: Using `indexOf` because the field may include 'WithPacking'.
    OFFER_SORTS.filter(({ field }) => field.indexOf(field) !== -1).pop()
  );
}

// Finds the "best" available offer, based on `field` and `order`, as close
// to the `moveDate` as possible.
// https://app.clubhouse.io/updater/story/6092/fe-offer-sorting
export const findBestOfferByMoveDate = (moveDate, sort) => {
  const sorts = getSorts(sort);
  const daysFromMoveDate = date =>
    Math.abs((new Date(date) - new Date(moveDate)) / DAY_MS);

  return offers =>
    orderBy(
      offers.filter(isAvailableOffer),
      [
        // Primary sort by proximity to the move date.
        ({ date }) => daysFromMoveDate(date),
        ...sorts.map(({ field }) => offer => offer[field]),
      ],
      ['asc'].concat(sorts.map(({ order }) => order))
    ).shift();
};

export const offersAreAvailable = offers =>
  !offers.every(({ id }) => id === null);

export const offerOnMoveDate = (moveDate, offers) => {
  return offers.filter(offer => {
    return offer.date === moveDate;
  });
};

/**
 *
 * Apply sorting to a set of offerGroups.
 * It will first sort the groups with available offers
 * on the top and then groups without below will not be sorted.
 * It will sort the available offer groups
 * by the sort field and the sort order.
 *
 * @param {Date} moveDate this is the movedate
 * @param {string} sort.field the field to sort by
 * @param {string} sort.order the order of the sort
 */
export const sortOfferGroupsByDate = (moveDate, sort) => offerGroups => {
  if (!offerGroups || offerGroups.length === 0) {
    return [];
  }
  const sorts = getSorts(sort);
  const findBestOffer = findBestOfferByMoveDate(moveDate, sort);
  const allOffers = offerGroups.reduce(
    (groups, offerGroup) => {
      const kind = offersAreAvailable(offerGroup.offers)
        ? 'available'
        : 'unavailable';
      groups[kind].push(offerGroup);
      return groups;
    },
    { available: [], unavailable: [] }
  );

  return orderBy(
    allOffers.available,
    sorts.map(({ field }) => ({ offers }) => findBestOffer(offers)[field]),
    sorts.map(({ order }) => order)
  ).concat(allOffers.unavailable);
};

export const getOfferGroupsByDate = moveDate => offerGroups => {
  if (!offerGroups || offerGroups.length === 0) {
    return [];
  }

  const offersOnMoveDate = offerGroups.reduce((groups, offerGroup) => {
    const { company, offers } = offerGroup;
    const logo_url = `${company.logo.base}/${company.logo.path}`;

    groups.push({
      moving_company: company.name,
      logo_url,
      price: offerOnMoveDate(moveDate, offers)[0].price,
      value_score: offerOnMoveDate(moveDate, offers)[0].valueScore,
    });
    return groups;
  }, []);

  return offersOnMoveDate;
};

export const findById = curry((id, array) => array.find(i => i.id === id));

// Conditional rendering helper for use with `hyperhtml`'s `wire`.
export const when = (bool, fn) => (bool ? fn() : '');
export const either = (bool, f, g) => (bool ? f() : g());

// https://stackoverflow.com/a/4819886/89594
// `typeof window` check is to keep Node from throwing (Protractor).
// TODO: Consider extracting to common and/or build with Modernizr
// https://github.com/Modernizr/Modernizr#building
export const IS_TOUCH_DEVICE = !!(
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints)
);

export const toggleClass = curry((className, element, value) => {
  value
    ? element.classList.add(className)
    : element.classList.remove(className);
});

export const toggleAttribute = curry((attrName, element, value) => {
  value
    ? element.setAttribute(attrName, '')
    : element.removeAttribute(attrName);
});

export function dateIsInThePast(dateString) {
  return Date.parse(dateString) - Date.now() < 0;
}

export function aWeekFromToday() {
  const aWeekLater = new Date();
  aWeekLater.setDate(aWeekLater.getDate() + 7);
  return dateFormat(aWeekLater, 'yyyy-mm-dd');
}

export function getTomorrowDateString() {
  let tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return `${tomorrow.getFullYear()}-${pad2DigitNum(
    tomorrow.getMonth() + 1
  )}-${pad2DigitNum(tomorrow.getDate())}`;
}

export function pad2DigitNum(num) {
  return `0${num}`.slice(-2);
}

export const equals = a => b => a === b;
export const any = arr => x => arr.some(fn => fn(x));

export const isProfileWidget = window.location.pathname.indexOf('/p/') === 0;

// TODO: Consolidate with formatNumber
export const formatNumberWithCommas = number =>
  number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export function sliceAndOrderByRating(comps) {
  const companies = comps.slice();
  const qualityRatingMap = {
    'A+': 1,
    A: 2,
    'A-': 3,
    'B+': 4,
    B: 5,
    'B-': 6,
  };
  const companyInOrder = companies.sort(
    (a, b) =>
      qualityRatingMap[a.rating.qualityRating] -
      qualityRatingMap[b.rating.qualityRating]
  );
  const companiesToDisplay = companyInOrder.slice(0, 4);
  return companiesToDisplay;
}

export const formatPrice = price => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumSignificantDigits: price < 100 ? 2 : 3,
  });
  return formatter.format(price);
};

// TODO: Consolidate with formatNumberWithCommas
export const formatNumber = number =>
  number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

export const mapCompanyForEmailConfirmation = company => ({
  id: company.id,
  name: company.name,
  description: company.description,
  qualityRating: company.rating.qualityRating,
  phone: company.phone,
  logo: company.logo.base ? `${company.logo.base}/${company.logo.path}` : '',
});

export const findRoute = (direction, routes, currentRoute) => {
  const map = {
    previous: routes[routes.indexOf(currentRoute) - 1],
    next: routes[routes.indexOf(currentRoute) + 1],
  };
  return map[direction];
};

export const calculateProgress = (routes, route) => {
  const totalRoutes = routes.length;
  const progressPerRoute = 100 / totalRoutes;
  const routeIndex = routes.indexOf(route) + 1;
  return progressPerRoute * routeIndex;
};

export const filterDiySequence = (routes, state) => {
  const { moveType } = state;
  if (moveType !== MOVE_TYPES.INTERSTATE.toLowerCase()) {
    return routes.filter(route => route !== ROUTES.diyAutoShip);
  }
  return routes;
};

export const isFunction = x =>
  Object.prototype.toString.call(x) === '[object Function]';
